import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, CheckCheck, CheckCircle2, ClipboardList, Flag, MessageSquare, Reply, School, ShieldAlert, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errorMessage, fetcher } from "@/lib/api";
import { timeAgo } from "@/lib/labels";

const KIND = {
  comment: { icon: MessageSquare, warna: "text-[#0B3D91] bg-blue-50 dark:bg-blue-950" },
  reply: { icon: Reply, warna: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950" },
  report: { icon: Flag, warna: "text-amber-700 bg-amber-50 dark:bg-amber-950" },
  moderation: { icon: ShieldAlert, warna: "text-[#E11D2E] bg-red-50 dark:bg-red-950" },
  pengajuan: { icon: ClipboardList, warna: "text-[#0B3D91] bg-blue-50 dark:bg-blue-950" },
  pengajuan_status: { icon: CheckCircle2, warna: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950" },
  pengajuan_ditolak: { icon: XCircle, warna: "text-[#E11D2E] bg-red-50 dark:bg-red-950" },
  sekolah_baru: { icon: School, warna: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950" },
  billing_reseller_ganti: { icon: School, warna: "text-amber-700 bg-amber-50 dark:bg-amber-950" },
};

const POLL_MS = 60000;

// Lonceng yang sama untuk tiga ruang: sekolah (endpoint keanggotaan), Pusat Kendali, dan Ruang Mitra
// (keduanya lewat kotak notifikasi generik /notifications).
export function NotificationBell() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const generic = !!me?.platform_role || !!me?.reseller;
  const fallback = generic ? (me?.platform_role ? "/pusat-kendali" : "/mitra") : "/sekolah";
  const endpoint = generic ? "/notifications" : "/school/notifications";
  const q = useQuery({
    queryKey: ["notifications", endpoint],
    queryFn: () => fetcher(`${endpoint}?limit=30`),
    refetchInterval: POLL_MS,
  });
  const baca = useMutation({
    mutationFn: async ({ id }) => (id ? api.post(`${endpoint}/${id}/read`) : api.post(`${endpoint}/read-all`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", endpoint] }),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const unread = q.data?.unread ?? 0;
  const daftar = q.data?.notifications ?? [];

  const buka = async (n) => {
    if (!n.read_at) await baca.mutateAsync({ id: n.id }).catch(() => {});
    if (n.link && n.link !== fallback) navigate(n.link);
    else navigate(fallback);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative shrink-0" aria-label={unread ? `${unread} notifikasi baru` : "Notifikasi"}
          aria-haspopup="dialog" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E11D2E] px-1 text-[10px] font-bold text-white" data-testid="notification-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 sm:w-[380px]" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <p className="text-sm font-bold">Notifikasi</p>
            <p className="text-xs text-muted-foreground" data-testid="notification-unread">
              {unread ? `${unread} baru` : "Tidak ada yang baru"}
            </p>
          </div>
          <Button variant="ghost" size="sm" disabled={!unread || baca.isPending} onClick={() => baca.mutate({ id: null })} data-testid="notification-read-all">
            <CheckCheck className="mr-1 h-4 w-4" /> Tandai dibaca
          </Button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {q.isLoading ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">Memuat notifikasi…</p>
          ) : q.error ? (
            <p className="px-4 py-6 text-center text-sm text-[#E11D2E]">{errorMessage(q.error)}</p>
          ) : !daftar.length ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">
              {generic
                ? "Belum ada notifikasi. Anda akan diberi tahu saat ada pengajuan baru atau keputusan penting."
                : "Belum ada notifikasi. Anda akan diberi tahu saat postingan Anda dikomentari atau ada laporan konten."}
            </p>
          ) : (
            <ul className="divide-y" data-testid="notification-list">
              {daftar.map((n) => {
                const k = KIND[n.kind] || KIND.comment;
                return (
                  <li key={n.id}>
                    <button type="button" onClick={() => buka(n)}
                      className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted ${n.read_at ? "" : "bg-blue-50/60 dark:bg-blue-950/30"}`}
                      data-testid={`notification-item-${n.kind}`}>
                      <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${k.warna}`}>
                        <k.icon className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold leading-snug">{n.title}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{n.body}</span>
                        <span className="mt-1 block text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                      </span>
                      {!n.read_at && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#E11D2E]" aria-hidden />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
